<template>
    <div v-if="isLoading"><loader :loading="true" v-if="isLoading" :fixed="true"></loader></div>
    <div v-else-if="forecastWindow && items && hasAccessRead(FUNCTIONALITY)" class="wrapper">
        <div><DefaultButton @click="$router.push(backLink)">&laquo; Back</DefaultButton></div>
        <div style="margin: 10px 0"><ForecastNameBanner :forecast="forecastWindow.forecast" /></div>
        <div>
            <div style="display: flex">
                <div>
                    <div class="title">{{ forecastWindow.zone.name }} <em v-if="isDisabledZone">(Deleted)</em></div>
                </div>
                <div v-if="canExport" style="margin-left: auto;">
                    <IconButton @click="exportXls" :width="'220px'"
                        ><template slot="icon"><ExportXlsIcon /></template> Download Results</IconButton
                    >
                </div>
            </div>
        </div>
        <div>
            <div class="page-section"><CalendarInfo :forecast="forecastWindow.forecast" :forecast-window="forecastWindow" /></div>
            <div class="page-section"><Contributors :forecast-window="forecastWindow" /></div>

            <div>
                <Comments :forecast="forecastWindow.forecast" :forecast-window="forecastWindow">
                    <div v-if="isForecastManager || isZonesSupervisor">
                        <div style="float: right">
                            <div v-if="canValidate">
                                <IconButton
                                    v-if="isZonesSupervisor"
                                    @click="validate"
                                    :width="'220px'"
                                    :background-color="FORECAST_WINDOW_COLORS.VALIDATED"
                                >
                                    <template slot="icon"><TickIcon /></template>
                                    Validate
                                </IconButton>
                            </div>
                            <br />
                            <div v-if="canReChallenge" style="margin-bottom: 15px">
                                <IconButton
                                    :disabled="this.forecastWindow.forecast.status === FORECAST_STATUS.CLOSED"
                                    @click="isReChallengeModalVisible = true"
                                    :width="'220px'"
                                    :background-color="FORECAST_WINDOW_COLORS.RE_CHALLENGED"
                                >
                                    <template slot="icon"><ReChallengeIcon /></template>
                                    Re-challenge <span class="rechallenge-cut">{{ forecastWindow.zone.name }}</span>
                                </IconButton>
                            </div>
                        </div>
                    </div>
                </Comments>
            </div>
        </div>

        <div
            v-if="forecastWindow.reChallengeMessage"
            class="notification SectionBorderTop"
            :style="{ borderColor: FORECAST_WINDOW_COLORS.RE_CHALLENGED, backgroundColor: '#fff6d7' }"
        >
            <div><ReChallengeIcon class="icon" /></div>
            <div>
                <div class="headline" :style="{ color: FORECAST_WINDOW_COLORS.RE_CHALLENGED }">Forecast Re-Challenged</div>
                <br />
                <div style="font-size: 12px;">
                    <div v-if="forecastWindow.reChallengedBy">
                        Re-Challenged by : {{ forecastWindow.reChallengedBy.lastname }}, {{ forecastWindow.reChallengedBy.firstname }}
                    </div>
                    <div v-if="forecastWindow.reChallengedDate">Re-Challenged date : {{ formatDate(forecastWindow.reChallengedDate) }}</div>
                </div>
                <br />
                <div :style="{ color: FORECAST_WINDOW_COLORS.RE_CHALLENGED }">Message :</div>
                <br />
                <div style="text-transform: none;"><MultiLineText :text="forecastWindow.reChallengeMessage" /></div>
            </div>
        </div>
        <div v-if="showValidationPendingMessage" class="notification SectionBorderTop">
            <div class="headline">The quantities are temporary and might change until final submission/validation</div>
        </div>
        <div class="row SectionBorderTop" v-if="isSubmittable || zoneReaderCanDownloadXls">
            <div class="col-sm-12 ">
                <div class="export">
                    <div style="padding-top: 5px; float: right">
                        <!-- Export XLS -->
                        <a v-if="exportXls" class="elementExcel" @click="exportXlsForImport"><ExportXlsForImportIcon style="height: 50px"/></a>

                        <!-- Import XLS -->
                        <label v-if="isSubmittable" class="elementExcel" for="myfile" style="cursor: pointer;"
                            ><UploadXlsIcon style="height: 50px"
                        /></label>
                        <input
                            v-if="isSubmittable"
                            type="file"
                            id="myfile"
                            name="myfile"
                            accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                            @change="loadFile"
                            style="display: none"
                        />
                    </div>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-lg-12">
                <ForecastTable>
                    <template slot="head-row-1">
                        <th
                            class="text-center"
                            :colspan="forecastWindow.forecast.recurrenceAmount + 3 + (forecastWindow.forecast.preseriesDate ? 1 : 0)"
                        >
                            {{ forecastWindow.zone.name }}
                        </th>
                    </template>
                    <template slot="head-row-2">
                        <th>Item</th>
                        <th>Description</th>
                        <th>Est. Price</th>
                        <th class="preseries" v-if="forecastWindow.forecast.preseriesDate && nonReactiveQuantitiesPreseries.length">
                            {{ getDateByFormat(forecastWindow.forecast.preseriesDate, "MM / YYYY") }}<br />PRESERIES
                        </th>
                        <th v-for="recurrence in forecastWindow.forecast.recurrenceAmount" v-bind:key="`rep_amount_${recurrence}`">
                            <div v-if="forecastWindow.forecast.datesRecurrence.length">
                                {{ getDateByFormat(forecastWindow.forecast.datesRecurrence[recurrence - 1], "MM / YYYY") }}
                            </div>
                        </th>
                    </template>
                    <template slot="body">
                        <tbody>
                            <tr v-for="item in items" v-bind:key="`${renderCycle}_${item.id}`" :class="{ isDisabled: isItemDisabled(item) }">
                                <td :style="item.color != null && item.color !== '' ? 'border-left: 15px solid ' + item.color : ''">
                                    <ItemImage :item="item" />
                                </td>
                                <td><ItemDescription :item="item" :forecast="forecastWindow.forecast" /></td>
                                <td class="text-left">{{ getPriceForItem(forecastWindow, item) }}</td>
                                <td v-if="forecastWindow.forecast.preseriesDate && nonReactiveQuantitiesPreseries.length">
                                    <div
                                        v-for="(quantity, index) in nonReactiveQuantitiesPreseries"
                                        v-if="quantity && quantity.item === item.id"
                                        :key="`preseries_${item.id}_${index}`"
                                        :id="`preseries_${item.id}_${index}`"
                                    >
                                        <QuantityInput
                                            @update="onValueUpdate($event, item.id, quantity.recurrence)"
                                            :value="quantity.quantity"
                                            :is-disabled="
                                                !isUpdatable ||
                                                    !(isZonesForecaster || isForecastManager || isZonesSupervisor || forecastsService.isGodMode()) ||
                                                    isObsolete(item) ||
                                                    isCancelled(item)
                                            "
                                            :item-id="quantity.item"
                                            :recurrence="quantity.recurrence"
                                            :index="parseInt(index)"
                                        />
                                    </div>
                                </td>
                                <td
                                    v-for="(quantity, index) in nonReactiveQuantities"
                                    v-if="quantity.item === item.id"
                                    :key="`${item.id}_${index}`"
                                    :id="`${item.id}_${index}`"
                                    class="text-left"
                                >
                                    <QuantityInput
                                        @update="onValueUpdate($event, item.id, quantity.recurrence)"
                                        :value="quantity.quantity"
                                        :is-disabled="!isUpdatable || isObsolete(item) || isCancelled(item)"
                                        :item-id="quantity.item"
                                        :recurrence="quantity.recurrence"
                                        :index="parseInt(index)"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </template>
                </ForecastTable>
            </div>
        </div>
        <div class="wrapper">
            <div style="float: right">
                Estimated total: <span class="total"> {{ estimatedTotalForecastWindow }}</span>
            </div>
        </div>
        <div class="comment">
            <div class="left">Comments / Special requirements</div>
            <div class="right"><textarea v-model="forecastWindow.comment" :disabled="!isSubmittable"> </textarea></div>
        </div>
        <div v-if="isSavable || isSubmittable" class="row">
            <div class="col-sm-12">
                <div style="text-align: right" class="submitBoutons">
                    <DefaultButton backgroundColor="#727a96" v-if="isUserSuperAdmin" @click="resetForecastWindow">Reset Forecast</DefaultButton>
                    <DefaultButton v-if="isSavable" @click="save()">Temporary save</DefaultButton>
                    <DefaultButton v-if="isSubmittable" @click="submit">Submit Forecast</DefaultButton>
                </div>
            </div>
        </div>
        <ReChallengeModal
            v-if="isReChallengeModalVisible"
            @closed="isReChallengeModalVisible = false"
            :forecast="forecastWindow.forecast"
            :provided-forecast-windows="[forecastWindow]"
            @rechallenged="loadForecastWindow"
        />
    </div>
</template>
<script>
import forecastsService from "@/components/forecasts/forecastsService";
import { FUNCTIONALITY } from "@/components/forecasts/constants";
import { FORECAST_WINDOW_STATUS, FORECAST_STATUS } from "@/components/forecasts/constants";
import ItemImage from "@/components/forecasts/elements/ItemImage";
import ItemDescription from "@/components/forecasts/elements/ItemDescription";
import ForecastTable from "@/components/forecasts/elements/table/ForecastTable";
import TryLoad from "@/mixins/TryLoadMixin";
import loader from "@/components/loader/loader";
import ForecastWindowValidator from "@/components/forecasts/validation/forecastWindowValidator";
import ExportXlsIcon from "@/components/forecasts/elements/icons/ExportXlsIcon";
import UploadXlsIcon from "@/components/forecasts/elements/icons/UploadXlsIcon";
import ExportXlsForImportIcon from "@/components/forecasts/elements/icons/ExportXlsForImportIcon";
import XlsFileIcon from "@/components/forecasts/elements/icons/XlsFileIcon";
import Globals from "@/components/globals";
import { mapActions, mapGetters } from "vuex";
import ReChallengeIcon from "@/components/forecasts/elements/icons/ReChallengeIcon";
import readXlsxFile from "read-excel-file";
import QuantityInput from "@/components/forecasts/elements/form/QuantityInput";
import ItemValidator from "@/components/forecasts/validation/itemValidator";
import { PROFILES } from "@/plugins/AccessUserPlugin";
import { ROUTES } from "@/components/forecasts/router";
import ReChallengeModal from "@/components/forecasts/elements/ReChallengeModal";
import ForecastNameBanner from "@/components/forecasts/elements/general/ForecastNameBanner";
import ForecastInfos from "@/components/forecasts/elements/general/ForecastInfos";
import DateInfosDetail from "@/components/forecasts/elements/general/DateInfosDetail";
import MultiLineText from "@/components/forecasts/elements/general/MultiLineText";
import DefaultButton from "@/components/forecasts/elements/form/DefaultButton";
import { FORECAST_WINDOW_COLORS } from "@/components/forecasts/utils/utilis";
import CalendarIcon from "@/components/forecasts/elements/icons/CalendarIcon";
import CalendarInfo from "@/components/forecasts/pages/sections/CalendarInfo";
import Contributors from "@/components/forecasts/pages/sections/Contributors";
import Comments from "@/components/forecasts/pages/sections/Comments";
import TickIcon from "@/components/forecasts/elements/icons/TickIcon";
import IconButton from "@/components/forecasts/elements/form/IconButton";
import { convertForecastInForecastWindow } from "@/components/forecasts/model/forecastWindow";
import forecastValidator from "@/components/forecasts/validation/forecastValidator";

export default {
    components: {
        IconButton,
        Comments,
        Contributors,
        CalendarInfo,
        DefaultButton,
        MultiLineText,
        DateInfosDetail,
        ForecastInfos,
        ForecastNameBanner,
        QuantityInput,
        ReChallengeIcon,
        ItemDescription,
        ItemImage,
        ForecastTable,
        loader,
        ExportXlsIcon,
        UploadXlsIcon,
        ExportXlsForImportIcon,
        XlsFileIcon,
        ReChallengeModal,
        CalendarIcon,
        TickIcon
    },
    props: ["ExportXlsIcon"],
    mixins: [TryLoad],
    data() {
        return {
            data: null,
            forecastWindow: null,
            forecastsService,
            items: [],
            sentence: "",
            FUNCTIONALITY,
            FORECAST_WINDOW_STATUS,
            FORECAST_STATUS,
            ForecastWindowValidator,
            renderCycle: 1,
            PROFILES,
            ROUTES,
            isReChallengeModalVisible: false,
            FORECAST_WINDOW_COLORS,
            isLoading: true
        };
    },
    async created() {
        if (this.brandZones === null || this.brandZones.length === 0) {
            await this.populateAdminBrandZones({ reload: false });
        }
        await this.loadForecastWindow();
        forecastsService.getItems(this.forecastWindow.forecast.id, true).then(items => {
            if (this.forecastWindow.multiValidation) {
                forecastsService.getItemsFwProperties(this.forecastWindow.forecast.id, this.forecastWindow.id).then(itemsProperties => {
                    this.itemWindowsProperties = itemsProperties;

                    if (this.itemWindowsProperties.length > 0) {
                        for (let itemPropertie of this.itemWindowsProperties) {
                            let item = items.find(item => item.id === itemPropertie.item.id);
                            if (itemPropertie.color != null && itemPropertie.color !== "") {
                                item.color = itemPropertie.color;
                            }
                            if (itemPropertie.order !== null && itemPropertie.color !== 0) {
                                item.order = itemPropertie.order;
                            }
                        }

                        items.sort((a, b) => {
                            return parseInt(a.order) - parseInt(b.order);
                        });
                    }
                });
            }
            this.items = items;
        });
        this.isLoading = false;
    },
    computed: {
        ...mapGetters("forecast", { brandZones: "getAdminBrandZones" }),
        nonReactiveQuantities() {
            return this.forecastWindow.quantities.filter(q => !q.isPreseries && q.recurrence > 0);
        },
        nonReactiveQuantitiesPreseries() {
            return this.forecastWindow.quantities.filter(q => q.isPreseries);
        },
        estimatedTotalForecastWindow() {
            return this.forecastWindow.quantities
                .map(q => {
                    let item = this.items.find(i => i.id === q.item);
                    if (!item) {
                        return 0;
                    }
                    return this.getPriceForItem(this.forecastWindow, item) * q.quantity;
                })
                .reduce((a, b) => a + b, 0)
                .toFixed(2);
        },
        isUpdatable() {
            return this.isSavable || this.isSubmittable;
        },
        isSavable() {
            return ForecastWindowValidator.canSave(this.forecastWindow, this.$accessUser);
        },
        isSubmittable() {
            return ForecastWindowValidator.canSubmit(this.forecastWindow, this.$accessUser);
        },
        isZoneReader() {
            return this.$accessUser.hasAccessByProfile(PROFILES.ZONE_READER);
        },
        zoneReaderCanDownloadXls() {
            return ForecastWindowValidator.zoneReaderCanDownloadXls(this.forecastWindow, this.$accessUser);
        },
        ...mapGetters({
            getBrand: "brand/getBrand"
        }),
        reChallengeMessages() {
            if (!this.forecastWindow.reChallengeMessage) {
                return "";
            }
            return this.forecastWindow.reChallengeMessage.split(/[\r\n]+/);
        },
        canReChallenge() {
            return (
                this.forecastWindow.status === FORECAST_WINDOW_STATUS.SUBMITTED &&
                !this.forecastWindow.isValidated &&
                forecastValidator.canReChallenge(this.forecastWindow.forecast)
            );
        },
        isForecastManager() {
            return this.hasAccessByProfile(PROFILES.FORECAST_MANAGER);
        },
        isZonesSupervisor() {
            return this.hasAccessByProfile(PROFILES.ZONES_SUPERVISOR);
        },
        isZonesForecaster() {
            return this.hasAccessByProfile(PROFILES.ZONE_FORECASTER);
        },
        isZonesReader() {
            return this.hasAccessByProfile(PROFILES.ZONE_READER);
        },
        isUserSuperAdmin() {
            return this.hasAccessSuperAdmin();
        },
        hasAccessToContributors() {
            return this.isZonesSupervisor || this.isForecastManager;
        },
        backLink() {
            if ((this.isZonesForecaster || this.isZonesReader) && !this.isUserSuperAdmin) {
                return `${ROUTES.BASE}`;
            } else {
                return `${ROUTES.BASE}/${this.forecastWindow.forecast.id}`;
            }
        },
        showValidationPendingMessage() {
            if (!this.isZonesSupervisor && !this.isForecastManager) {
                return false;
            }

            if (this.forecastWindow.multiValidation && !this.forecastWindow.isValidated && this.isForecastManager) {
                return true;
            }

            return this.forecastWindow.status !== FORECAST_WINDOW_STATUS.SUBMITTED;
        },
        canValidate() {
            return (
                this.forecastWindow.multiValidation &&
                !this.forecastWindow.isValidated &&
                this.forecastWindow.status === FORECAST_WINDOW_STATUS.SUBMITTED
            );
        },
        canExport() {
            return this.forecastWindow.status === FORECAST_WINDOW_STATUS.SUBMITTED;
        },
        isDisabledZone() {
            const brandZone = this.brandZones.find(bz => bz.zone.id === this.forecastWindow.zone.id);
            if (!brandZone) {
                return true;
            }
            return !brandZone.active;
        }
    },
    methods: {
        ...mapActions("forecast", { refreshForecast: "refreshForecast" }),
        ...mapActions("forecast", { populateAdminBrandZones: "populateAdminBrandZones" }),
        async resetForecastWindow() {
            const res = await this.dialogConfirm("Are you sure you want to reset these quantities ?", "Reset", "Cancel");
            if (res === true) {
                const resetForecast = Object.assign({}, this.forecastWindow);
                resetForecast.quantities = [];
                this.save(resetForecast).then(r => {
                    this.loadForecastWindow();
                    this.renderCycle++;
                });
            }
        },
        async loadForecastWindow() {
            const id = this.$route.params.id;
            let [forecastWindow] = await this.tryLoad(() => Promise.all([forecastsService.getForecastWindow(id, true)]));
            // if (this.isZonesForecaster) {
            forecastWindow = convertForecastInForecastWindow(forecastWindow);
            // }
            this.forecastWindow = forecastWindow;
        },
        async submit() {
            const res = await this.dialogConfirm(
                "Are you sure you would like to submit your forecast ? Please note that you will not be able to modify quantities anymore for this forecast after this step.",
                "Submit",
                "Cancel"
            );
            if (res === true) {
                this.forecastWindow.status = FORECAST_WINDOW_STATUS.SUBMITTED;
                await this.tryLoad(() => forecastsService.updateForecastWindow(this.forecastWindow));
                this.forecastWindow = await this.tryLoad(() => forecastsService.getForecastWindow(this.forecastWindow.id, true));
                if (this.hasAccessByProfile(PROFILES.FORECAST_MANAGER)) {
                    await this.refreshForecast(this.forecastWindow.forecast);
                }
                this.dialogSuccess("Forecast has been submitted");
            }
        },
        async save(forecast = null) {
            if (!forecast) {
                forecast = this.forecastWindow;
            }
            console.log(forecast);
            forecast.status = FORECAST_WINDOW_STATUS.SAVED;
            await this.tryLoad(() => forecastsService.updateForecastWindow(forecast));
            this.forecastWindow = await this.tryLoad(() => forecastsService.getForecastWindow(forecast.id, true));
            this.dialogSuccess("Forecast has been updated");
        },
        async exportXls() {
            const resp = await this.tryLoad(() => forecastsService.getForecastWindowExportXLS(this.$route.params.id));
            this.dowloadXls(resp, "Forecast-");
        },
        async exportXlsForImport() {
            const resp = await this.tryLoad(() => forecastsService.getForecastWindowExportXLSForImport(this.$route.params.id));
            this.dowloadXls(resp, "Forecast-");
        },
        async validate() {
            const res = await this.dialogConfirm("Are you sure you would like to validate the forecast ?", "Submit", "Cancel");
            if (res === true) {
                await this.tryLoad(() => forecastsService.validateForecastWindow(this.forecastWindow));
                this.forecastWindow.isValidated = true;
                this.dialogSuccess("Forecast has been validated");
                await this.refreshForecast(this.forecastWindow.forecast);
            }
        },
        dowloadXls(resp, namePrefix) {
            const a = document.createElement("a");
            a.href = URL.createObjectURL(new Blob([resp], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
            const zone = this.forecastWindow.zone.name.replace(/[^a-zA-Z0-9]/g, "_");
            const ref = this.forecastWindow.forecast.reference.replace(/[^a-zA-Z0-9/-]/g, "_");
            a.setAttribute(
                "download",
                namePrefix +
                    this.getBrand.name +
                    "-" +
                    ref +
                    "-" +
                    zone +
                    "-" +
                    Globals.moment(new Date()).format("YYYY-MM-DD-HH-mm-ss-SSS") +
                    ".xlsx"
            );
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        },
        loadFile($event) {
            if (
                $event.target.files[0].name
                    .split(".")
                    .pop()
                    .toLowerCase() !== "xlsx"
            ) {
                window.alert("Invalid format. The application only accepts ‘xlsx’ file format");
            } else {
                readXlsxFile($event.target.files[0]).then(rows => {
                    // Vérification du nom du forecast dans le fichier
                    if (
                        !(
                            (this.forecastWindow.forecast.name + " " + this.forecastWindow.forecast.reference).toLowerCase() ===
                                rows[2][1].toLowerCase() && this.forecastWindow.zone.name.toLowerCase() === rows[5][1].toLowerCase()
                        )
                    ) {
                        this.$swal({
                            // text: "This excel file does not match your current forecast." + "\n" + " Please make sure you have selected the correct file.",
                            html: this.$t("launchForecast.forecastWindow.xlsDontMatch"),
                            type: "warning",
                            confirmButtonText: "OK",
                            confirmButtonColor: "#41b4e8",
                            allowOutsideClick: false,
                            allowEscapeKey: false,
                            showCancelButton: false
                        });

                        return;
                    }

                    // Vérification du template code
                    if (this.forecastWindow.lastTemplateControlCode && this.forecastWindow.lastTemplateControlCode !== rows[0][3]) {
                        this.$swal({
                            html: this.$t("launchForecast.forecastWindow.redownloadXls"),
                            type: "warning",
                            confirmButtonText: "OK",
                            confirmButtonColor: "#41b4e8",
                            allowOutsideClick: false,
                            allowEscapeKey: false,
                            showCancelButton: false
                        });

                        return;
                    }

                    const quantitiesStartingRow = 9;
                    const quantityRows = rows.slice(quantitiesStartingRow, rows.length);

                    this.fill(quantityRows);
                });
            }
        },
        /**
         * Remplit les valeurs des quantités existantes depuis les nouvelles en provenance du fichier Excel importé
         * @param quantityRows Tableau des nouvelles quantités
         */
        fill(quantityRows) {
            // Les quantités démarrent à l'index 7
            // Avant, ce sont les infos du component
            const startingQuantityIndex = 7;

            // Permet de savoir si le fichier excel a la colonne de preserie ou non
            const xlsHasPreseriesColumn =
                this.forecastWindow.forecast.preseriesDate &&
                this.forecastWindow.quantities.filter(quantity => quantity.recurrence === 0 && quantity.isPreseries === true).length > 0;

            // Pour chaque ligne de quantités en provenance du fichier Excel
            quantityRows.forEach(newQuantityRow => {
                const sapCode = newQuantityRow[0];

                // On recup le component correspondant au sap code trouvé
                const item = this.items.find(item => item !== null && item.clientSapRef === sapCode);
                if (item) {
                    // On n'édite pas les quantités si l'item est Obsolete ou Cancelled
                    if (!ItemValidator.isObsolete(item) && !ItemValidator.isCancelled(item)) {
                        // On recup toutes les quantités liées à l'objet trouvé
                        // Si le forecast n'a pas de preseries, on ne recup pas les récurrences 0
                        const existingQuantities = this.forecastWindow.quantities.filter(qty => {
                            if (!xlsHasPreseriesColumn) {
                                return qty.item === item.id && qty.recurrence !== 0;
                            }
                            return qty.item === item.id;
                        });

                        // On trie les quantités existantes par recurrence
                        existingQuantities.sort((a, b) => {
                            if (a.recurrence < b.recurrence) {
                                return -1;
                            } else if (a.recurrence > b.recurrence) {
                                return 1;
                            }

                            return 0;
                        });

                        // On recup uniquement les valeurs des nouvelles quantités
                        const newQuantities = newQuantityRow.slice(startingQuantityIndex, newQuantityRow.length);

                        // Pour chaque quantité venant du fichier Excel
                        newQuantities.forEach((newQuantityValue, i) => {
                            if (existingQuantities[i]) {
                                // Si la nouvelle valeur de la quantité existe et est positive, on l'attribue
                                if (newQuantityValue && newQuantityValue >= 0) {
                                    if (existingQuantities[i].recurrence === 0 && existingQuantities[i].isPreseries === false) {
                                        existingQuantities[i].quantity = 0;
                                    } else {
                                        existingQuantities[i].quantity = newQuantityValue;
                                    }

                                    // Sinon on met 0
                                } else {
                                    existingQuantities[i].quantity = 0;
                                }
                            }
                        });
                    }
                }
            });

            this.renderCycle++;
        },
        getPriceForItem(forecastWindow, item) {
            for (let price of item.prices) {
                if (price.zone.id === forecastWindow.zone.id) {
                    return price.value;
                }
            }
            return "";
        },
        onValueUpdate(payload, item, recurrence) {
            const quantity = this.forecastWindow.quantities.find(q => q.item === item && q.recurrence === recurrence);
            if (quantity) {
                quantity.quantity = payload.value;
            }
        },
        isItemDisabled(item) {
            return ItemValidator.isDisabled(item);
        },
        isObsolete(item) {
            return ItemValidator.isObsolete(item);
        },
        isCancelled(item) {
            return ItemValidator.isCancelled(item);
        }
    }
};
</script>
<style scoped lang="scss">
@import "../css/forecasts";
.wrapper {
    text-transform: uppercase;
    width: 100%;
}
.table-wrapper {
    margin-top: 20px;
}
.page-section {
    padding: 15px 0;
}
.total {
    display: inline-block;
    padding: 5px 25px;
    margin: 10px 10px;
    color: $color3;
    background-color: $color7;
}
.notification {
    border: 3px solid #b37c83;
    display: flex;
    background-color: #fbf2f4;
    padding: 20px;
    margin: 15px;
    .headline {
        color: #b37c83;
        font-weight: bold;
        text-transform: uppercase;
    }
    .icon {
        width: 50px;
        height: 50px;
        padding-right: 15px;
    }
}
.comment {
    display: flex;
    width: 100%;
    min-height: 80px;
    margin-bottom: 20px;
    .left {
        width: 200px;
        background-color: $table_color_1;
        padding: 10px;
        color: #ffffff;
        text-transform: uppercase;
    }
    .right {
        width: 100%;
        textarea {
            width: 100%;
            height: 100%;
            box-sizing: border-box;
            padding: 10px;
        }
    }
}
.forecast-comments {
    font-size: 1.4em;
    font-style: italic;
    font-weight: lighter;
    margin-bottom: 20px;
    white-space: pre-line;
}
h1.title {
    margin-bottom: 2px;
}

div.title {
    display: flex;
    align-items: center;
    em {
        margin-left: 10px;
    }
}
.elementExcel {
    margin: 0 5px 0 5px;
}
.isDisabled {
    background-color: $color_background_disabled;
}
.export {
    margin-left: auto;
    padding-top: 15px;
}
.comment {
    text-transform: none;
}
.rechallenge-cut {
    width: 100px;
    overflow: hidden;
    display: inline-block;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.submitBoutons {
    margin-left: -5px;
    margin-right: -5px;
    button {
        margin: 5px;
    }
}
</style>



// WEBPACK FOOTER //
// ForecastWindowDetail.vue?68981cd9